export type CollectionRevisionKind =
  | 'INITIAL'
  | 'MANUAL_CORRECTION'
  | 'RECURRING_SYNC';

export type CollectionWorkflowRevisionKind = Exclude<
  CollectionRevisionKind,
  'RECURRING_SYNC'
>;

export type CollectionRevisionStatus =
  | 'DRAFT'
  | 'SEALED'
  | 'SUBMITTED'
  | 'CANCELLED';

export type CollectionRevisionTargetStatus =
  | 'PENDING'
  | 'IN_PROGRESS'
  | 'COMPLETED';

export type CollectionFileValidationStatus = 'UNKNOWN' | 'USABLE' | 'UNUSABLE';

export type CollectionFileValidationDisposition =
  | 'ALREADY_BOUND'
  | 'REUSED_EVIDENCE'
  | 'CREATED_EVIDENCE';

export type CollectionRevisionMeanType = 'CONNECTION' | 'UPLOAD';
export type TargetFileSetCommitOutcome = 'COMPLETED' | 'NO_OP';

export interface TargetFileIdRequest {
  readonly file_id: string;
  readonly resolved_inputs: Readonly<Record<string, string>>;
}

export interface CommitTargetFileSetRequest {
  readonly files: readonly TargetFileIdRequest[];
}

export interface ValidateTargetFilesRequest {
  readonly files: readonly TargetFileIdRequest[];
}

export interface CollectionRevisionValidationFileDto {
  readonly file_id: string;
  readonly disposition: CollectionFileValidationDisposition;
  readonly validation_profile: string;
  readonly validation_profile_version: number;
  readonly usable: boolean;
  readonly detected_language: string | null;
  readonly detected_revenue_type: string | null;
  readonly detected_revenue_subtype: string | null;
  readonly decision: Readonly<Record<string, unknown>> | null;
  readonly error_code: string | null;
  readonly message: string | null;
  readonly resolved_inputs: Readonly<Record<string, string>>;
  readonly required_inputs: readonly CollectionValidationRequiredInput[];
}

export type StatementOriginRequiredInputConstraint =
  | 'ALL_VISIBLE'
  | 'BRAZIL_PRO';

export interface SourceStatementCurrencyRequiredInput {
  readonly key: 'source_statement_currency';
}

export interface StatementOriginRequiredInput {
  readonly key: 'statement_origin_id';
  readonly catalog: 'STATEMENT_ORIGINS';
  readonly constraint: StatementOriginRequiredInputConstraint;
  readonly allow_create: boolean;
}

export type CollectionValidationRequiredInput =
  | SourceStatementCurrencyRequiredInput
  | StatementOriginRequiredInput;

export const SOURCE_STATEMENT_CURRENCY_INPUT_KEY = 'source_statement_currency';
export const STATEMENT_ORIGIN_INPUT_KEY = 'statement_origin_id';

export interface CollectionRevisionValidationResponse {
  readonly files: readonly CollectionRevisionValidationFileDto[];
  readonly all_usable: boolean;
}

export interface CollectionRevisionDto {
  readonly id: string;
  readonly collection_id: string;
  readonly revision_no: number;
  readonly kind: CollectionRevisionKind;
  readonly status: CollectionRevisionStatus;
  readonly based_on_revision_id: string | null;
  readonly editable: boolean;
  readonly allowed_actions: readonly string[];
}

export interface CollectionWorkflowTargetDto {
  readonly target_id: string;
  readonly order_idx_snapshot: number;
  readonly revenue_type: string;
  readonly revenue_subtype: string | null;
  readonly mean_type_snapshot: CollectionRevisionMeanType;
  readonly target_status: CollectionRevisionTargetStatus;
  readonly acquisition_outcome: string | null;
}

export interface CollectionWorkflowDto {
  readonly collection_id: string;
  readonly revision_id: string;
  readonly revision_no: number;
  readonly revision_kind: CollectionWorkflowRevisionKind;
  readonly revision_status: CollectionRevisionStatus;
  readonly editable: boolean;
  readonly allowed_actions: readonly string[];
  readonly etag: string;
  readonly targets: readonly CollectionWorkflowTargetDto[];
}

export interface CollectionWorkflowResponse {
  readonly workflow: CollectionWorkflowDto;
}

export interface CollectionRevisionTargetFileDto {
  readonly file_id: string;
  readonly ordinal: number;
  readonly name: string | null;
  readonly media_type: string | null;
  readonly size_bytes: number | null;
  readonly validation_status: CollectionFileValidationStatus;
}

export interface CollectionRevisionTargetFileSetDto {
  readonly collection_id: string;
  readonly revision_id: string;
  readonly target_id: string;
  readonly revision_status: CollectionRevisionStatus;
  readonly target_status: CollectionRevisionTargetStatus;
  readonly editable: boolean;
  readonly required_files_count: number;
  readonly allowed_actions: readonly string[];
  readonly files: readonly CollectionRevisionTargetFileDto[];
}

export interface CollectionRevisionNavigationDto {
  readonly collection_id: string;
  readonly revision_id: string;
  readonly target_id: string;
}

export interface CollectionRevisionFileSetCommitDto {
  readonly displayed_revision_id: string;
  readonly outcome: TargetFileSetCommitOutcome;
  readonly effective_revision: CollectionRevisionDto;
  readonly target_file_set: CollectionRevisionTargetFileSetDto;
  readonly navigation: CollectionRevisionNavigationDto;
}

export interface CollectionRevisionResponse {
  readonly revision: CollectionRevisionDto;
}

export interface CollectionRevisionTargetFileSetResponse {
  readonly target_file_set: CollectionRevisionTargetFileSetDto;
}

export interface CollectionRevisionFileSetCommitResponse {
  readonly file_set_commit: CollectionRevisionFileSetCommitDto;
}

export interface StageTargetFileResponse {
  readonly file_id: string;
  readonly status: 'STAGED';
  readonly name: string | null;
  readonly media_type: string | null;
  readonly size_bytes: number | null;
  readonly expires_at: string;
}

export interface CollectionRevisionCurrentDto {
  readonly revision: CollectionRevisionDto | null;
  readonly target_file_set: CollectionRevisionTargetFileSetDto | null;
}

export interface VersionedCollectionRevision {
  readonly revision: CollectionRevisionDto;
  readonly revisionEtag: string;
}

export interface VersionedCollectionRevisionTargetFileSet {
  readonly targetFileSet: CollectionRevisionTargetFileSetDto;
  readonly targetEtag: string;
  readonly revisionEtag: string;
}

export interface VersionedCollectionRevisionFileSetCommit {
  readonly commit: CollectionRevisionFileSetCommitDto;
  readonly targetEtag: string;
  readonly revisionEtag: string;
}

export interface CollectionIntegrationAuthorization {
  readonly shouldRedirect: boolean;
  readonly url: string;
  readonly resultStatus: string;
  readonly expiresAt: string | null;
}
